import {Button} from '@wandb/components';
import React, {type ReactElement} from 'react';

import {isInIframe} from '../../util/window';
import {Tooltip} from '../../weave/components/Tooltip';
import {
  type AgentChatEntryPoint,
  useWBAgentController,
} from '../chat/controller';
import {useAgentChatReadonly} from '../chat/useAgentChatReadonly';
import {AGENT_NAME} from './constants';
import {WBAgentEntryPointIconButtonView} from './WBAgentEntryPointIconButtonView';

type EntryPointProps = {
  'data-dd-action-name': string;
  'data-test'?: string;
  entryPoint: AgentChatEntryPoint;
  isDisabled?: boolean;
  onChatStarted?: () => void;
  placeholder: string;
};

type WBAgentEntryPointButtonProps = EntryPointProps & {
  label?: string;
};

export const WBAgentEntryPointTooltip = ({
  children,
  label,
  side = 'left',
}: {
  children: ReactElement<React.HTMLAttributes<HTMLElement>, string>;
  label: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
}) => <Tooltip content={label} noTriggerWrap side={side} trigger={children} />;

export const WBAgentEntryPointButton = ({
  'data-dd-action-name': ddActionName,
  'data-test': dataTest,
  entryPoint,
  isDisabled,
  label,
  onChatStarted,
  placeholder,
}: WBAgentEntryPointButtonProps) => {
  const {isAvailable, startChat} = useWBAgentController();
  const isReadonly = useAgentChatReadonly();

  return isAvailable && !isInIframe() ? (
    <Button
      data-dd-action-name={ddActionName}
      data-test={dataTest}
      isDisabled={isDisabled || isReadonly}
      startIcon="lightboard-solid"
      variant="secondary"
      onPress={() => {
        const result = startChat([], {
          delivery: 'draft',
          entryPoint,
          target: 'new',
          placeholder,
        });
        if (result.ok) {
          onChatStarted?.();
        }
      }}>
      {label ?? `Ask ${AGENT_NAME}`}
    </Button>
  ) : null;
};

type WBAgentEntryPointIconButtonProps = EntryPointProps & {
  'aria-label': string;
};

export const WBAgentEntryPointIconButton = ({
  'aria-label': ariaLabel,
  'data-dd-action-name': ddActionName,
  'data-test': dataTest,
  entryPoint,
  isDisabled,
  onChatStarted,
  placeholder,
}: WBAgentEntryPointIconButtonProps) => {
  const {isAvailable, startChat} = useWBAgentController();
  const isReadonly = useAgentChatReadonly();

  return isAvailable && !isInIframe() ? (
    <WBAgentEntryPointIconButtonView
      aria-label={ariaLabel}
      data-dd-action-name={ddActionName}
      data-test={dataTest}
      isDisabled={isDisabled || isReadonly}
      onPress={() => {
        const result = startChat([], {
          delivery: 'draft',
          entryPoint,
          target: 'new',
          placeholder,
        });
        if (result.ok) {
          onChatStarted?.();
        }
      }}
    />
  ) : null;
};
